import type {
  AskCitation, AskEvent, AskRequest, AskTurn, KnowledgeKind, MemoryFact, MemoryFactsPage, MemoryFactsQuery, MemoryTopic, MemoryTopics, PersonId, SessionId,
} from "@don/wire";

import type { Backend } from "../backend/adapter.js";
import { sessionPath } from "../routing/route.js";
import { atText, type StepAt } from "../session/reading.js";

/**
 * The kinds, as their words, in display order, then `Other`: a kind, not a refusal
 * (`wire::KnowledgeKind`, issue 229). The Type view's section headings and index links
 * (design-spec §6, issue 281). Every wire kind's word is on this list (`backend/adapter.ts`
 * `KIND_WORD`, typed against it). Asserted by `memory.test.ts`.
 */
export const KINDS = ["Rule", "Gotcha", "Where to find", "Contact", "Decision", "Other"] as const;

/**
 * The wire kinds the Type view reads, one `facts(id, { kind })` each, in `KINDS` order
 * (`KIND_WORD` maps each to its word there; asserted by `memory.test.ts`).
 */
export const SECTION_KINDS: readonly KnowledgeKind[] = ["rule", "gotcha", "where_to_find", "contact", "decision", "other"];

/**
 * The memory seam (issue 276, design-spec §9.1): what the Knowledge page reads about one
 * person's memory. The wire types are the frozen memory contract (`crates/wire/src/memory.rs`).
 */
export interface MemorySource {
  topics(personId: PersonId): Promise<MemoryTopics>;
  facts(personId: PersonId, query: MemoryFactsQuery): Promise<MemoryFactsPage>;
  /** Yields every event of one answer and always ends with `done` or `failed`. */
  ask(personId: PersonId, request: AskRequest, signal?: AbortSignal): AsyncIterable<AskEvent>;
}

/**
 * Every read rejects and every answer fails: the source with nothing behind it.
 * `defaultMemorySource` replaces all three with the backend's. Asserted by `memory.test.ts`.
 */
export const UNWIRED_MEMORY: MemorySource = {
  topics: () => Promise.reject(new Error("memory: no source for topics")),
  facts: () => Promise.reject(new Error("memory: no source for facts")),
  async *ask() {
    yield { event: "failed" };
  },
};

/**
 * A person's topics as the list draws them (design-spec §9.1): the one whose newest fact was
 * added last first, equal dates by name. A new array; the answer is left as it came.
 * Asserted by `memory.test.ts`.
 */
export function sortTopics(topics: readonly MemoryTopic[]): MemoryTopic[] {
  return [...topics].sort(
    (a, b) => b.last_added_at_ms - a.last_added_at_ms || (a.topic < b.topic ? -1 : a.topic > b.topic ? 1 : 0),
  );
}

/**
 * A person's facts as a topic's group draws them (design-spec §9.1, D17): newest recorded
 * first, equal dates by id. A new array. Asserted by `memory.test.ts`.
 */
export function sortFacts(facts: readonly MemoryFact[]): MemoryFact[] {
  return [...facts].sort(
    (a, b) => b.recorded_at_ms - a.recorded_at_ms || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );
}

/**
 * The facts already shown with the next page's (D33): one copy of each id, then `sortFacts`,
 * so a newer fact on a later page lands above ones already shown. Facts are matched by `id`
 * only, never by content: two facts that disagree stay two facts (D7). Asserted by
 * `memory.test.ts`.
 */
export function mergePage(loaded: readonly MemoryFact[], page: MemoryFactsPage): MemoryFact[] {
  const seen = new Set(loaded.map((f) => f.id));
  return sortFacts([...loaded, ...page.facts.filter((f) => !seen.has(f.id) && (seen.add(f.id), true))]);
}

/** A moment link's `aria-label` and `title` (design-spec §6). Asserted by `memory.test.ts`. */
export function momentLabel(at: StepAt): string {
  if ("bookend" in at) return `Open the session's ${at.bookend} in a new tab`;
  return `Open the session at ${atText(at)} in a new tab`;
}

/**
 * A citation marker's `aria-label` and `title` (design-spec §6): "Source <n>: open the session
 * at 14:22 in a new tab", or "…the session's intro…". Asserted by `memory.test.ts`.
 */
export function citationLabel(n: number, at: StepAt): string {
  const moment = momentLabel(at);
  return `Source ${n}: ${moment.charAt(0).toLowerCase()}${moment.slice(1)}`;
}

/**
 * Where a moment's link goes (issue 280, design-spec §9.6): a timed moment opens the session at
 * that point (`?at=`), a bookend opens the session at its start.
 */
export function momentPath(sessionId: SessionId, at: StepAt): string {
  return "bookend" in at ? sessionPath(sessionId) : sessionPath(sessionId, at.atMs);
}

/**
 * The library as the Knowledge page last heard it (issue 285, design-spec §9.5): still reading,
 * failed, or read, with the session ids it answered and when that read was sent and answered,
 * both on the Console's clock (`KnowledgeScreen`'s `now`).
 */
export type LibraryView =
  | { readonly state: "reading" }
  | { readonly state: "failed" }
  | { readonly state: "read"; readonly sessionIds: ReadonlySet<SessionId>; readonly sentAtMs: number; readonly answeredAtMs: number };

/**
 * Where a fact's moment or a citation links, or `null` when its recording is deleted (issue 285,
 * design-spec §9.5, D31). Deleted only when a library read **sent** after the item was shown
 * (`shownAtMs`) lacks the session: a read sent earlier, even one answered later, may predate the
 * session, and a library reading or failed cannot know (P4). A row present, even with its video
 * expired, links. Asserted by `memory.test.ts`.
 */
export function linkOf(sessionId: SessionId, at: StepAt, library: LibraryView, shownAtMs: number): string | null {
  const gone = library.state === "read" && library.sentAtMs > shownAtMs && !library.sessionIds.has(sessionId);
  return gone ? null : momentPath(sessionId, at);
}

/**
 * The source the Knowledge page reads when no test hands one in (`App.tsx`): topics, facts and
 * ask from the backend's routes (issues 278, 279, 282). Asserted by `memory.test.ts`.
 */
export function defaultMemorySource(backend: Pick<Backend, "memoryTopics" | "memoryFacts" | "ask">): MemorySource {
  return {
    topics: (personId) => backend.memoryTopics(personId),
    facts: (personId, query) => backend.memoryFacts(personId, query),
    ask: (personId, request, signal) => backend.ask(personId, request, signal),
  };
}

/** One `part` of an answer as the conversation keeps it. */
export interface AnswerPart {
  readonly text: string;
  readonly citations: readonly AskCitation[];
}

/** One turn of an Ask conversation: the owner's question, or an answer as it streams. */
export type Turn =
  | { readonly role: "owner"; readonly text: string }
  | { readonly role: "assistant"; readonly parts: readonly AnswerPart[]; readonly state: "streaming" | "done" | "failed" };

/** One answer's sources, numbered (`numberSources`). */
export interface NumberedSource {
  readonly n: number;
  readonly citation: AskCitation;
}

/**
 * An answer's sources as its markers and Sources list number them (design-spec §9.1): each
 * distinct `fact_id` once, numbered from 1 in the order it first appears across the parts.
 * Asserted by `memory.test.ts`.
 */
export function numberSources(parts: readonly AnswerPart[]): NumberedSource[] {
  const seen = new Map<string, NumberedSource>();
  for (const part of parts) {
    for (const citation of part.citations) {
      if (!seen.has(citation.fact_id)) seen.set(citation.fact_id, { n: seen.size + 1, citation });
    }
  }
  return [...seen.values()];
}

/** How the store asks: one answer's events for one request (a `MemorySource.ask` bound to a person). */
export type Asker = (request: AskRequest, signal: AbortSignal) => AsyncIterable<AskEvent>;

/** The conversations of the page, by scope. Built by `createConversationStore`. */
export interface ConversationStore {
  subscribe(listener: () => void): () => void;
  /** Every scope's turns; a new map after every change and the same one between changes. */
  snapshot(): ReadonlyMap<string, readonly Turn[]>;
  turns(scope: string): readonly Turn[];
  /** Whether the scope's last answer is still arriving. */
  streaming(scope: string): boolean;
  /**
   * Ask `question` in `scope`, unless it is blank once trimmed or the scope is streaming
   * (then `false`, and nothing changes).
   */
  send(scope: string, question: string, ask: Asker): boolean;
  /**
   * Ask the scope's last question again (Try again, D20), only when its last turn is a `failed`
   * answer: that turn is removed and the same messages are sent. Otherwise `false`, and nothing
   * changes.
   */
  retry(scope: string, ask: Asker): boolean;
  /** Abort every stream and empty every scope (sign-out, `App`'s `clearPage`). */
  clear(): void;
}

const NO_TURNS: readonly Turn[] = [];

/**
 * The page's Ask conversations (issue 282, design-spec §9.4), one list of turns per opaque
 * scope (`person:<id>`).
 *
 * **It lives in `App`, not in the Knowledge screen, because an answer outlives the screen**:
 * an answer keeps arriving into its scope while the owner looks at another person or another
 * tab, and is there when they come back (ux-spec D39, issue 283). So the screen reads it
 * (`useSyncExternalStore`) and never owns it.
 *
 * `send` pushes the owner's turn and an empty `streaming` answer, then asks with
 * `askRequest(turns so far, question)`. Each `part` is appended; `done` and `failed` end the
 * turn; an ask that throws, or ends with neither and was not aborted, ends it `failed` (issue
 * 284). `retry` replaces a last `failed` answer with a new one asked with the same request.
 * Nothing but `clear()` aborts a stream: choosing another person or tab leaves it arriving into
 * its own scope (issue 283). `clear()` aborts every running stream's signal, empties every
 * scope and bumps a generation; a stream writes only while its generation is current, so a
 * source that ignores the abort recreates nothing, not even in a newer answer in the same scope
 * (issue 284). Asserted by `memory.test.ts`.
 */
export function createConversationStore(): ConversationStore {
  let conversations: ReadonlyMap<string, readonly Turn[]> = new Map();
  const listeners = new Set<() => void>();
  // Every running stream's controller, so `clear()` can abort them all.
  const running = new Set<AbortController>();
  // Bumped by `clear()`: a stream started before it writes nothing after it.
  let generation = 0;
  const turnsOf = (scope: string) => conversations.get(scope) ?? NO_TURNS;
  const write = (scope: string, turns: readonly Turn[]) => {
    conversations = new Map(conversations).set(scope, turns);
    for (const listener of listeners) listener();
  };
  // The answer being written is always the scope's last turn: a scope sends nothing new while
  // it streams.
  const update = (scope: string, change: (turn: Extract<Turn, { role: "assistant" }>) => Turn) => {
    const turns = turnsOf(scope);
    const last = turns[turns.length - 1];
    if (last?.role !== "assistant" || last.state !== "streaming") return;
    write(scope, [...turns.slice(0, -1), change(last)]);
  };
  const streaming = (scope: string) => {
    const last = turnsOf(scope).at(-1);
    return last?.role === "assistant" && last.state === "streaming";
  };

  async function stream(scope: string, ask: Asker, request: AskRequest) {
    const controller = new AbortController();
    const mine = generation;
    const end = (state: "done" | "failed") => {
      if (mine === generation) update(scope, (turn) => ({ ...turn, state }));
    };
    running.add(controller);
    try {
      for await (const event of ask(request, controller.signal)) {
        if (mine !== generation) return;
        if (event.event === "part") {
          const part = { text: event.text, citations: event.citations };
          update(scope, (turn) => ({ ...turn, parts: [...turn.parts, part] }));
        } else {
          end(event.event);
          return;
        }
      }
      // No terminal event: an answer that stopped, unless the page stopped it (issue 284).
      if (!controller.signal.aborted) end("failed");
    } catch {
      end("failed");
    } finally {
      running.delete(controller);
    }
  }

  // Push an empty streaming answer after `turns` (which end in the owner's question) and ask.
  const start = (scope: string, turns: readonly Turn[], request: AskRequest, ask: Asker) => {
    write(scope, [...turns, { role: "assistant", parts: [], state: "streaming" }]);
    void stream(scope, ask, request);
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
    snapshot: () => conversations,
    turns: turnsOf,
    streaming,
    send(scope, question, ask) {
      const text = question.trim();
      if (text.length === 0 || streaming(scope)) return false;
      start(scope, [...turnsOf(scope), { role: "owner", text }], askRequest(turnsOf(scope), text), ask);
      return true;
    },
    retry(scope, ask) {
      const turns = turnsOf(scope);
      const last = turns.at(-1);
      const question = turns.at(-2);
      if (last?.role !== "assistant" || last.state !== "failed" || question?.role !== "owner") return false;
      // Nothing before the last turn has changed since it was asked, so this is that request.
      start(scope, turns.slice(0, -1), askRequest(turns.slice(0, -2), question.text), ask);
      return true;
    },
    clear() {
      generation += 1;
      for (const controller of running) controller.abort();
      running.clear();
      conversations = new Map();
      for (const listener of listeners) listener();
    },
  };
}

/** The most turns one Ask request carries, the new question included (design-spec §9.4). */
export const ASK_TURNS_MAX = 40;
/** The longest assistant text one Ask request carries (design-spec §9.4). */
export const ASK_TURN_MAX_CHARS = 4000;

/**
 * The wire request for asking `question` after `turns` (issue 283, design-spec §9.4, D32).
 * Owner turns carry `citations: []`. Assistant turns, partial and failed ones included, carry
 * `answerText(parts)` cut to `ASK_TURN_MAX_CHARS` and each distinct `fact_id` they streamed
 * once, in first-appearance order (`numberSources`), so a follow-up can resolve "the second
 * one". The question is appended. Over `ASK_TURNS_MAX`, whole owner-plus-assistant pairs are
 * dropped from the front, so the first message is always the owner's. Asserted by
 * `memory.test.ts`.
 */
export function askRequest(turns: readonly Turn[], question: string): AskRequest {
  const messages: AskTurn[] = turns.map((turn) =>
    turn.role === "owner"
      ? { role: "owner", text: turn.text, citations: [] }
      : {
          role: "assistant",
          text: answerText(turn.parts).slice(0, ASK_TURN_MAX_CHARS),
          citations: numberSources(turn.parts).map((source) => source.citation),
        },
  );
  messages.push({ role: "owner", text: question, citations: [] });
  let from = 0;
  while (messages.length - from > ASK_TURNS_MAX || messages[from]?.role !== "owner") from += messages[from]?.role === "owner" ? 2 : 1;
  return { messages: messages.slice(from) };
}

/** An answer's parts as one text: each trimmed, empty ones skipped, joined by one space. */
export function answerText(parts: readonly AnswerPart[]): string {
  return parts
    .map((part) => part.text.trim())
    .filter((text) => text.length > 0)
    .join(" ");
}
